import { queryClient } from '@/queryClient';
import { abortAllPendingRequests } from '@/common/interceptor/sessionAbortController';
import webSocketService from '@/context/webSocketService';

export const STORAGE_SYNC_KEY = '__zoomlogi_storage_reset_sync__';
export const BROADCAST_CHANNEL_NAME = 'zoomlogi_auth_storage_sync';

export interface ClearStorageOptions {
  /**
   * Whether to broadcast the reset to other open tabs via BroadcastChannel and StorageEvent.
   * Defaults to true. Should be set to false when reacting to an incoming cross-tab broadcast.
   */
  broadcast?: boolean;
  /**
   * Whether to redirect the current window to '/login' after clearing storage.
   * Defaults to false.
   */
  navigateToLogin?: boolean;
  /**
   * Whether to flush and purge Redux Persist storage.
   * Defaults to true.
   */
  purgePersisted?: boolean;
}

let isClearing = false;
const beforeClearHooks: Array<() => void> = [];

/**
 * Registers a callback that runs synchronously at the start of
 * clearAllStorageAndState, while the current user's token still exists --
 * e.g. the activity beacon sending its pending events. Registered from
 * index.tsx rather than imported here, which would create an import cycle
 * (interceptor -> storageCleanup -> beacon -> interceptor).
 */
export function onBeforeStorageClear(hook: () => void): void {
  if (!beforeClearHooks.includes(hook)) beforeClearHooks.push(hook);
}
let broadcastChannel: BroadcastChannel | null = null;

function getBroadcastChannel(): BroadcastChannel | null {
  if (
    typeof window === 'undefined' ||
    typeof BroadcastChannel === 'undefined'
  ) {
    return null;
  }
  if (!broadcastChannel) {
    try {
      broadcastChannel = new BroadcastChannel(BROADCAST_CHANNEL_NAME);
    } catch {
      broadcastChannel = null;
    }
  }
  return broadcastChannel;
}

/**
 * Broadcasts a storage clear notification to all other open tabs/windows.
 */
export function broadcastStorageReset(): void {
  const channel = getBroadcastChannel();
  if (channel) {
    try {
      channel.postMessage({
        type: 'CLEAR_STORAGE',
        timestamp: Date.now(),
      });
    } catch {
      // Best-effort
    }
  }

  if (typeof window !== 'undefined' && window.localStorage) {
    try {
      window.localStorage.setItem(STORAGE_SYNC_KEY, String(Date.now()));
    } catch {
      // Best-effort
    }
  }
}

/**
 * Completely resets and clears:
 * 1. localStorage (removes tokens, persisted auth, cached preferences)
 * 2. sessionStorage (removes child/preview tokens, session flags, preview stores)
 * 3. Redux store/state (dispatches resetState() and resetTabs())
 * 4. Redux Persist storage (flushes, purges, and strips persist:root from both storages)
 * 5. In-flight network requests (aborts active axios calls)
 * 6. React Query cache and WebSocket connections
 * 7. Broadcasts the reset to other open tabs if requested
 */
export async function clearAllStorageAndState(
  options: ClearStorageOptions = {}
): Promise<void> {
  const {
    broadcast = true,
    navigateToLogin = false,
    purgePersisted = true,
  } = options;

  if (isClearing) {
    return;
  }
  isClearing = true;

  try {
    // 0. Run before-clear hooks while this user's token still exists
    for (const hook of beforeClearHooks) {
      try {
        hook();
      } catch {
        // Best-effort
      }
    }

    // 1. Abort pending HTTP requests
    try {
      abortAllPendingRequests();
    } catch {
      // Guard against environments where interceptor is not loaded
    }

    // 2. Disconnect active WebSockets
    try {
      webSocketService?.disconnect?.();
    } catch {
      // Guard
    }

    // 3. Clear React Query cache
    try {
      queryClient?.clear?.();
    } catch {
      // Guard
    }

    // 4. Broadcast to other tabs BEFORE localStorage.clear()
    if (broadcast) {
      broadcastStorageReset();
    }

    // 5. Clear browser localStorage synchronously
    if (typeof window !== 'undefined' && window.localStorage) {
      try {
        window.localStorage.clear();
      } catch {
        // Fallback for restricted storage environments
        try {
          window.localStorage.removeItem('@token');
          window.localStorage.removeItem('@refreshToken');
          window.localStorage.removeItem('persist:root');
        } catch {
          // Ignore
        }
      }
    }

    // 6. Clear browser sessionStorage synchronously
    if (typeof window !== 'undefined' && window.sessionStorage) {
      try {
        window.sessionStorage.clear();
      } catch {
        // Fallback
        try {
          window.sessionStorage.removeItem('@previewToken');
          window.sessionStorage.removeItem('@previewRefreshToken');
          window.sessionStorage.removeItem('persist:root');
        } catch {
          // Ignore
        }
      }
    }

    // Ensure persist:root is stripped from both storages
    if (typeof window !== 'undefined') {
      try {
        window.localStorage?.removeItem('persist:root');
      } catch {
        // Ignore
      }
      try {
        window.sessionStorage?.removeItem('persist:root');
      } catch {
        // Ignore
      }
    }

    // 7. Reset Redux store in memory
    try {
      const { store, resetState } = await import('@/redux-toolkit/store');
      const { resetTabs } = await import('@/redux-toolkit/slice/tabSlice');
      store?.dispatch?.(resetState());
      store?.dispatch?.(resetTabs());
    } catch {
      // Guard
    }

    // 8. Purge and flush Redux Persist storage
    if (purgePersisted) {
      try {
        const { persistor } = await import('@/redux-toolkit/store');
        await persistor?.flush?.();
        await persistor?.purge?.();
      } catch {
        // Guard
      }
    }

    // 9. Navigate to /login if requested
    if (navigateToLogin && typeof window !== 'undefined') {
      try {
        if (window.location?.pathname !== '/login') {
          if (typeof window.location?.replace === 'function') {
            window.location.replace('/login');
          } else {
            window.location.href = '/login';
          }
        }
      } catch {
        try {
          window.location.href = '/login';
        } catch {
          // Ignore
        }
      }
    }
  } finally {
    isClearing = false;
  }
}

/**
 * Sets up listeners across tabs (BroadcastChannel + StorageEvent) to detect
 * when storage is cleared by any other tab, and triggers a full local reset.
 *
 * @param onRemoteClear - Optional callback invoked after a remote clear is executed.
 * @returns Cleanup function to unregister listeners.
 */
export function setupCrossTabStorageSync(
  onRemoteClear?: () => void
): () => void {
  if (typeof window === 'undefined') {
    return () => {};
  }

  const handleRemoteTrigger = async () => {
    if (isClearing) return;
    await clearAllStorageAndState({
      broadcast: false,
      navigateToLogin: true,
    });
    onRemoteClear?.();
  };

  // 1. BroadcastChannel listener
  const channel = getBroadcastChannel();
  const onChannelMessage = (event: MessageEvent) => {
    if (event?.data?.type === 'CLEAR_STORAGE') {
      handleRemoteTrigger();
    }
  };

  if (channel) {
    try {
      channel.addEventListener('message', onChannelMessage);
    } catch {
      // Fallback
    }
  }

  // 2. Storage event listener (fires in other tabs when localStorage is modified)
  const onStorageEvent = (event: StorageEvent) => {
    if (!event) return;

    // A localStorage.clear() in another tab yields event.key === null or ''
    const isClearCall = event.key === null || event.key === '';
    // Dedicated sync key update
    const isSyncKey = event.key === STORAGE_SYNC_KEY;
    // Auth token removal in another tab
    const isTokenRemoved = event.key === '@token' && !event.newValue;

    if (isClearCall || isSyncKey || isTokenRemoved) {
      handleRemoteTrigger();
    }
  };

  try {
    window.addEventListener('storage', onStorageEvent);
  } catch {
    // Guard
  }

  return () => {
    if (channel) {
      try {
        channel.removeEventListener('message', onChannelMessage);
      } catch {
        // Ignore
      }
    }
    try {
      window.removeEventListener('storage', onStorageEvent);
    } catch {
      // Ignore
    }
  };
}
